import QRCode from 'qrcode';
import { hideToast, showToast } from './toast';
import { showPosterPopup } from './popup';

// 引入海报背景和新花城logo图片资源
const posterBg = require('../assets/images/poster-bg.jpg');
const huachengLogo = require('../assets/images/huacheng-logo.png');
const background = require('../assets/images/background.jpg');

// 定义画布和海报空余区域的默认尺寸
const defaultCanvasWidth = 1080;
const defaultcanvasHeight = 1920;
const posterEmptyX = 180;
const posterEmptyY = 480;
const posterEmptyWidth = 720;
const posterEmptyHeight = 1065;
const posterEmptyMargin = 25;
const blurBackgroundEmptyMargin = 15;

// 获取当前页面的分享链接
const shareUrl = window.location.href.split('#')[0];

/**
 * 生成二维码图片的Promise
 * @returns 返回一个Promise，解析为HTMLImageElement类型的二维码图片
 */
const createQrcode = (): Promise<HTMLImageElement> => {
  return new Promise((resolve) => {
    QRCode.toDataURL(shareUrl, {
      errorCorrectionLevel: 'Q',
      width: 400,
      margin: 1.5,
    }).then((src) => {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        resolve(image);
      };
      if (image.complete) {
        resolve(image);
      }
    });
  });
};

/**
 * 加载图片资源的Promise
 * @param src 图片的来源地址
 * @returns 返回一个Promise，解析为HTMLImageElement类型的图片对象
 */
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = src;
    image.setAttribute('crossOrigin', 'anonymous');
    image.onload = () => {
      resolve(image);
    };
    if (image.complete) {
      resolve(image);
    }
  });
};

/**
 * 计算背景虚化图片的位置和尺寸
 * @param image 虚化背景的图片对象
 * @returns 返回一个数组，包含虚化背景在海报中的x, y坐标和宽度、高度
 */
const calcBlurBackgroundPosition = (image: HTMLImageElement) => {
  const { naturalWidth: epaperWidth } = image;
  const width = posterEmptyWidth - blurBackgroundEmptyMargin * 2;
  const ratio = width / epaperWidth;
  const height = ratio * epaperWidth * 1.5 + 15;
  const x = posterEmptyX + blurBackgroundEmptyMargin;
  const y = posterEmptyY + (posterEmptyHeight - height) / 2 - 15;
  return [x, y, width, height];
};

/**
 * 计算电子报在海报中的位置和尺寸
 * @param image 电子报的图片对象
 * @returns 返回一个数组，包含电子报在海报中的x, y坐标和宽度、高度
 */
const calcEpaperPosition = (image: HTMLImageElement) => {
  const { naturalWidth: epaperWidth, naturalHeight: epaperHeight } = image;
  const width = posterEmptyWidth - posterEmptyMargin * 2;
  const ratio = width / epaperWidth;
  const height = ratio * epaperHeight;
  const x = posterEmptyX + posterEmptyMargin;
  const y = posterEmptyY + (posterEmptyHeight - height) / 2;
  return [x, y, width, height];
};

/**
 * 创建海报的主函数
 * @param epaper 电子报的图片地址
 */
const createPoster = async (epaper: string) => {
  const container = document.querySelector('.container');
  if (!container) {
    return;
  }
  // 显示生成海报中的加载提示
  showToast({
    content: '正在生成海报...',
    type: 'loading',
    duration: 0,
  });
  // 并行加载二维码、海报背景、新花城logo和电子报图片资源
  const [qrcodeRes, posterBgRes, logoRes, epaperRes, backgroundRes] =
    await Promise.all([
      createQrcode(),
      loadImage(posterBg),
      loadImage(huachengLogo),
      loadImage(epaper),
      loadImage(background),
    ]);
  // 创建画布并绘制海报
  const posterCanvas = document.createElement('canvas');
  posterCanvas.width = defaultCanvasWidth;
  posterCanvas.height = defaultcanvasHeight;
  posterCanvas.className = 'poster-canvas';
  container.appendChild(posterCanvas);
  const ctx = posterCanvas.getContext('2d');
  if (!ctx) {
    return;
  }
  // 在画布上绘制各元素
  ctx.drawImage(posterBgRes, 0, 0, defaultCanvasWidth, defaultcanvasHeight);
  ctx.drawImage(qrcodeRes, 0, 0, 400, 400, 432, 1575, 220, 220);
  const { naturalWidth: logoWidth, naturalHeight: logoHeight } = logoRes;
  ctx.drawImage(logoRes, 0, 0, logoWidth, logoHeight, 512, 1675, 60, 60);
  const { naturalWidth: bgNWidth, naturalHeight: bgNHeight } = backgroundRes;
  // 计算并绘制电子报的位置和尺寸
  const { naturalWidth: eWidth, naturalHeight: eHeight } = epaperRes;
  if (eWidth > eHeight) {
    const [bgx, bgy, bgwidth, bgheight] =
      calcBlurBackgroundPosition(backgroundRes);
    ctx.filter = 'blur(50px)';
    ctx.drawImage(
      backgroundRes,
      0,
      0,
      bgNWidth,
      bgNHeight,
      bgx,
      bgy,
      bgwidth,
      bgheight
    );
    ctx.filter = 'none';
  }
  const [x, y, width, height] = calcEpaperPosition(epaperRes);
  ctx.drawImage(epaperRes, 0, 0, eWidth, eHeight, x, y, width, height);
  // 将画布转换为DataURL，用以展示在弹出窗口中
  const image = await posterCanvas.toDataURL('image/jpeg');
  showPosterPopup(image);
  // 关闭加载提示
  hideToast();
  posterCanvas.remove();
};

export default createPoster;
